/**
 * Shared trash-point scoring logic — mirrors matchup-engine/index.ts exactly.
 * Used by the matchup detail view to compute per-player TP from raw game stats.
 */

export const DEFAULT_TRASH_WEIGHTS: Record<string, number> = {
  // Batting Trash
  __derived_outs: 3.00, strikeouts: 5.50, gidp: 2.00, left_on_base: 1.00,
  caught_stealing: 5.00,
  // Batting Anti
  hits: -2.50, __derived_total_bases: -1.50, home_runs: -7.00,
  runs_scored: -2.00, rbi: -2.00, walks_drawn: -1.50,
  hit_by_pitch: 0.50, stolen_bases: -4.50,
  // Fielding
  errors: 7.00, passed_balls: 3.00, throwing_errors: 4.00,
  // Pitching Trash
  walks_allowed: 5.00, home_runs_allowed: 8.00, earned_runs: 5.50,
  blown_saves: 22.00, pitcher_losses: 10.00, hit_batsmen: 4.00,
  wild_pitches: 4.00, balks: 5.00, hits_allowed: 2.00,
  // Pitching Anti
  pitcher_strikeouts: -2.00, pitcher_wins: -14.00, saves: -13.00,
  holds: -2.00, shutouts: -12.50, complete_games: -10.00,
};

/** Map scoring rule stat/id names to player_game_stats column names */
const STAT_TO_COLUMN: Record<string, string> = {
  strikeout: 'strikeouts', strikeouts: 'strikeouts',
  error: 'errors', errors: 'errors',
  gidp: 'gidp', caught_stealing: 'caught_stealing',
  lob: 'left_on_base', left_on_base: 'left_on_base',
  earned_run: 'earned_runs', earned_runs: 'earned_runs',
  walk_allowed: 'walks_allowed', walks: 'walks_allowed', walks_allowed: 'walks_allowed',
  wild_pitch: 'wild_pitches', wild_pitches: 'wild_pitches',
  blown_save: 'blown_saves', blown_saves: 'blown_saves',
  hit_batters: 'hit_batsmen', hit_batsman: 'hit_batsmen', hit_batsmen: 'hit_batsmen',
  hr_allowed: 'home_runs_allowed', home_run_allowed: 'home_runs_allowed', home_runs_allowed: 'home_runs_allowed',
  balk: 'balks', balks: 'balks',
  passed_ball: 'passed_balls', passed_balls: 'passed_balls',
  hit: 'hits', hits: 'hits',
  home_run: 'home_runs', home_runs: 'home_runs',
  rbi: 'rbi', run_scored: 'runs_scored', runs_scored: 'runs_scored',
  stolen_base: 'stolen_bases', stolen_bases: 'stolen_bases',
  pitcher_strikeout: 'pitcher_strikeouts', pitcher_strikeouts: 'pitcher_strikeouts',
  pitcher_win: 'pitcher_wins', pitcher_wins: 'pitcher_wins',
  losses: 'pitcher_losses', pitcher_losses: 'pitcher_losses',
  save: 'saves', saves: 'saves',
  holds: 'holds',
  hits_allowed: 'hits_allowed',
  throwing_errors: 'throwing_errors',
  outfield_assist: 'outfield_assists', outfield_assists: 'outfield_assists',
  doubles: 'doubles', triples: 'triples',
  walks_drawn: 'walks_drawn',
  hit_by_pitch: 'hit_by_pitch',
  outs: '__derived_outs',
  total_bases: '__derived_total_bases',
  pitcher_outs: '__derived_pitcher_outs',
  complete_game: 'complete_games', complete_games: 'complete_games',
  shutout: 'shutouts', shutouts: 'shutouts',
  no_hitter: '__event_no_hitter',
  perfect_game: '__event_perfect_game',
  triple_play: '__event_triple_play',
};

/** Build scoring weights from league category_config.scoring_rules */
export function buildScoringWeights(categoryConfig: any): Record<string, number> {
  if (!categoryConfig?.scoring_rules || !Array.isArray(categoryConfig.scoring_rules)) {
    return DEFAULT_TRASH_WEIGHTS;
  }
  const weights: Record<string, number> = {};
  for (const rule of categoryConfig.scoring_rules) {
    if (!rule.isEnabled) continue;
    const col = STAT_TO_COLUMN[rule.stat] || STAT_TO_COLUMN[rule.id] || null;
    if (!col) continue;
    weights[col] = rule.pointValue;
  }
  return weights;
}

/** Calculate trash points for a single stat row — mirrors matchup-engine exactly */
export function calculateTrashPoints(
  stats: Record<string, number>,
  weights: Record<string, number>,
): number {
  let points = 0;
  for (const [col, weight] of Object.entries(weights)) {
    if (col === '__derived_outs') {
      const outs = (Number(stats.at_bats) || 0) - (Number(stats.hits) || 0);
      points += outs * weight;
    } else if (col === '__derived_total_bases') {
      const tb = (Number(stats.hits) || 0) + (Number(stats.doubles) || 0) + 2 * (Number(stats.triples) || 0) + 3 * (Number(stats.home_runs) || 0);
      points += tb * weight;
    } else if (col === '__derived_pitcher_outs') {
      const pOuts = Math.round((Number(stats.innings_pitched) || 0) * 3);
      points += pOuts * weight;
    } else {
      points += (Number(stats[col]) || 0) * weight;
    }
  }
  // Grand slam check
  if ((stats.home_runs || 0) > 0 && (stats.rbi || 0) >= 4) {
    points += weights.grand_slam || -10;
  }
  return points;
}

/** Get a flat map of column_name → { label, weight } for UI display */
export function getWeightEntries(weights: Record<string, number>): { key: string; label: string; weight: number }[] {
  const LABEL_MAP: Record<string, string> = {
    strikeouts: 'K', errors: 'E', gidp: 'GIDP', caught_stealing: 'CS',
    earned_runs: 'ER', walks_allowed: 'BB', wild_pitches: 'WP', blown_saves: 'BS',
    hit_batsmen: 'HBP', home_runs_allowed: 'HRA', balks: 'BK', passed_balls: 'PB',
    hits: 'H', home_runs: 'HR', rbi: 'RBI', runs_scored: 'R', stolen_bases: 'SB',
    walks_drawn: 'BB', doubles: '2B', triples: '3B',
    pitcher_strikeouts: 'SO', pitcher_wins: 'W', saves: 'SV', outfield_assists: 'OFA',
    innings_pitched: 'IP',
    __derived_outs: 'OUT', __derived_total_bases: 'TB', __derived_pitcher_outs: 'PO',
    hits_allowed: 'HA', pitcher_losses: 'L', holds: 'HLD',
    left_on_base: 'LOB', hit_by_pitch: 'HBP', throwing_errors: 'TE',
    complete_games: 'CG', shutouts: 'SHO',
  };
  return Object.entries(weights)
    .filter(([, w]) => w !== 0)
    .map(([key, weight]) => ({ key, label: LABEL_MAP[key] || key, weight }));
}
